import axios from 'axios';
import { env } from '../config/env';
import { handleAuthExpiry } from '../lib/authRedirect';

// Axios client for the platform-service wallet APIs. This is a DIFFERENT
// backend from the gpustack training gateway, but it accepts the same login JWT
// the training portal already stores under `training_token`. The base URL
// already includes the /api/v1 path segment; endpoints below are appended.
const api = axios.create({
  baseURL: env.PLATFORM_API_URL,
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('training_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      handleAuthExpiry('training_token', '/training');
    }
    return Promise.reject(error);
  }
);

// ---- Types ----------------------------------------------------------------

// Wallet summary from GET /wallet. Amounts are in INR. Only the fields the
// portal actually renders are typed; the backend returns more.
export interface WalletSummary {
  balance: number;
  held_balance?: number;
  available_balance: number;
  currency?: string;
  account_type?: string;
  is_low_balance?: boolean;
  hourly_run_rate?: number;
  // Payment methods the backend actually supports for this user (e.g.
  // ["razorpay"] or ["razorpay","paypal"]). Used to avoid showing a method
  // whose backend isn't configured.
  allowed_topup_methods?: string[];
}

export interface Transaction {
  id: string;
  wallet_id?: string;
  type: 'credit' | 'debit';
  amount: number;
  currency?: string;
  original_amount?: number;
  original_currency?: string;
  exchange_rate?: number;
  description: string;
  reference_id?: string;
  reference_type?: string;
  razorpay_order_id?: string;
  razorpay_payment_id?: string;
  created_at: string;
}

export interface TransactionsResponse {
  transactions: Transaction[];
  total: number;
  page?: number;
  page_size?: number;
}

// POST /wallet/topup response — fed straight into the Razorpay checkout options.
export interface TopUpResponse {
  order_id: string;
  amount: number;
  currency: string;
  key: string;
  name: string;
  description: string;
  prefill?: {
    email?: string;
    contact?: string;
    name?: string;
  };
}

// POST /wallet/paypal/create-order response — a redirect-based approval flow.
export interface PayPalCreateOrderResponse {
  approval_url?: string;
  order_id?: string;
}

export interface PayPalCaptureResponse {
  amount_credited?: number;
  order_id?: string;
}

// ---- Service --------------------------------------------------------------

export interface BuilderCreditsApplication {
  email: string;
  project_description: string;
  github_url?: string;
  colab_url?: string;
  linkedin_url?: string;
  phone?: string;
}

export const walletApi = {
  getWallet: () => api.get<WalletSummary>('/wallet'),

  topUp: (amount: number) =>
    api.post<TopUpResponse>('/wallet/topup', { amount, payment_method: 'card' }),

  verifyPayment: (orderId: string, paymentId: string, signature: string) =>
    api.post('/wallet/payment/verify', {
      razorpay_order_id: orderId,
      razorpay_payment_id: paymentId,
      razorpay_signature: signature,
    }),

  getTransactions: (page = 1, pageSize = 20) =>
    api.get<TransactionsResponse>(`/wallet/transactions?page=${page}&page_size=${pageSize}`),

  createPayPalOrder: (amountUSD: number, returnUrl?: string, cancelUrl?: string) =>
    api.post<PayPalCreateOrderResponse>('/wallet/paypal/create-order', {
      amount_usd: amountUSD,
      ...(returnUrl ? { return_url: returnUrl } : {}),
      ...(cancelUrl ? { cancel_url: cancelUrl } : {}),
    }),

  capturePayPalOrder: (orderId: string) =>
    api.post<PayPalCaptureResponse>('/wallet/paypal/capture-order', { order_id: orderId }),
};

export const builderCreditsApi = {
  apply: (data: BuilderCreditsApplication) =>
    api.post('/public/builder-credits/apply', data),
};
